import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

// Recadrage avant envoi — la version web n'avait jusqu'ici AUCUNE étape de recentrage/zoom,
// contrairement au mobile qui bénéficie du crop natif d'expo-image-picker (allowsEditing).
// Fenêtre fixe (viewportW x viewportH), image "cover" par défaut, glisser pour repositionner,
// molette/slider pour zoomer. Le rendu final est exporté en WebP via canvas, à une résolution
// fixe raisonnable — jamais la taille brute du fichier d'origine.
export default function ImageCropModal({
  file, aspect, shape = "rect", title, onCancel, onConfirm,
}: {
  file: File;
  aspect: number; // largeur / hauteur du cadre
  shape?: "circle" | "rect";
  title: string;
  onCancel: () => void;
  onConfirm: (blob: Blob) => void;
}) {
  const viewportW = 320;
  const viewportH = Math.round(viewportW / aspect);
  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const [imgSize, setImgSize] = useState<{ w: number; h: number } | null>(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setImgUrl(url);
    const img = new Image();
    img.onload = () => setImgSize({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Échelle "cover" de base (1x du slider) : l'image remplit toujours entièrement la fenêtre.
  const baseScale = imgSize ? Math.max(viewportW / imgSize.w, viewportH / imgSize.h) : 1;
  const effectiveScale = baseScale * scale;
  const dispW = imgSize ? imgSize.w * effectiveScale : viewportW;
  const dispH = imgSize ? imgSize.h * effectiveScale : viewportH;
  const maxOffsetX = Math.max(0, (dispW - viewportW) / 2);
  const maxOffsetY = Math.max(0, (dispH - viewportH) / 2);
  const clamp = (v: number, max: number) => Math.max(-max, Math.min(max, v));

  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: offset.x, origY: offset.y };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setOffset({
      x: clamp(dragRef.current.origX + dx, maxOffsetX),
      y: clamp(dragRef.current.origY + dy, maxOffsetY),
    });
  };
  const onPointerUp = () => { dragRef.current = null; };
  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    setScale(s => Math.max(1, Math.min(3, s - e.deltaY * 0.002)));
  };

  // Reclamp si le zoom réduit la marge de déplacement disponible (sinon l'image peut se
  // retrouver décentrée en zoomant après un déplacement, avec un bord visible du cadre).
  useEffect(() => {
    setOffset(o => ({ x: clamp(o.x, maxOffsetX), y: clamp(o.y, maxOffsetY) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale, imgSize]);

  const confirm = () => {
    if (!imgSize || !imgUrl) return;
    setExporting(true);
    const img = new Image();
    img.onload = () => {
      // Résolution d'export fixe et raisonnable — jamais la taille brute d'origine.
      const outW = shape === "circle" ? 600 : Math.round(600 * aspect);
      const outH = shape === "circle" ? 600 : Math.round(600 / aspect);
      const canvas = document.createElement("canvas");
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext("2d");
      if (!ctx) { setExporting(false); return; }
      // Zone visible dans le cadre, exprimée en pixels de l'image source d'origine.
      const srcScale = effectiveScale;
      const visibleW = viewportW / srcScale;
      const visibleH = viewportH / srcScale;
      const srcX = imgSize.w / 2 - offset.x / srcScale - visibleW / 2;
      const srcY = imgSize.h / 2 - offset.y / srcScale - visibleH / 2;
      ctx.drawImage(img, srcX, srcY, visibleW, visibleH, 0, 0, outW, outH);
      canvas.toBlob(blob => {
        setExporting(false);
        if (blob) onConfirm(blob);
      }, "image/webp", 0.85);
    };
    img.src = imgUrl;
  };

  return ReactDOM.createPortal(
    <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.75)", zIndex: 4000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ backgroundColor: "white", borderRadius: 20, padding: 22, width: "100%", maxWidth: 400, boxSizing: "border-box" }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 14, textAlign: "center" }}>{title}</div>

        <div
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
          onWheel={onWheel}
          style={{
            width: viewportW, height: viewportH, margin: "0 auto", position: "relative",
            overflow: "hidden", backgroundColor: "#111",
            borderRadius: shape === "circle" ? "50%" : 12,
            cursor: dragRef.current ? "grabbing" : "grab",
            touchAction: "none", userSelect: "none",
          }}
        >
          {imgUrl && imgSize && (
            <img
              src={imgUrl}
              alt=""
              draggable={false}
              style={{
                position: "absolute",
                left: `calc(50% + ${offset.x}px - ${dispW / 2}px)`,
                top: `calc(50% + ${offset.y}px - ${dispH / 2}px)`,
                width: dispW, height: dispH, maxWidth: "none",
                pointerEvents: "none",
              }}
            />
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 16 }}>
          <span style={{ fontSize: 12, color: "#999" }}>🔍</span>
          <input
            type="range" min={1} max={3} step={0.05} value={scale}
            onChange={e => setScale(Number(e.target.value))}
            style={{ flex: 1 }}
          />
        </div>
        <div style={{ fontSize: 11.5, color: "#999", textAlign: "center", marginTop: 6 }}>Glissez pour repositionner, molette pour zoomer</div>

        <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
          <button onClick={onCancel} disabled={exporting} style={{ flex: 1, padding: "12px", borderRadius: 12, border: "1px solid #E0E0E0", backgroundColor: "white", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>Annuler</button>
          <button onClick={confirm} disabled={exporting || !imgSize} style={{ flex: 1, padding: "12px", borderRadius: 12, border: "none", backgroundColor: "#0a0a0a", color: "white", cursor: "pointer", fontSize: 14, fontWeight: 600, opacity: exporting ? 0.6 : 1 }}>
            {exporting ? "..." : "Valider"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
